import { useState, useEffect, useCallback } from "react";
import { formatDateUTC } from "../utils/dates";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ErrorBanner from "./ErrorBanner";
import { canManageAfes, AFE_CATEGORIES, AFE_TYPES, afeStatusColor } from "../utils/afe";

// Well page: every AFE on this well with Budget / Actual / Over-Under /
// % spent and the per-category breakdown. Admin/supervisor can add, edit
// and delete (the backend enforces the same rule). Actual always comes
// from the server (costs tagged to the AFE) — never computed here.
const API = process.env.REACT_APP_API_URL;
const cyan = "#00f5ff";
const border = "rgba(0,245,255,0.15)";
const card = "rgba(0,245,255,0.03)";

const inputStyle = {
  width: "100%",
  backgroundColor: "rgba(0,245,255,0.05)",
  color: "white",
  border: "1px solid rgba(0,245,255,0.2)",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "14px",
  minHeight: "44px",
  boxSizing: "border-box",
};
const labelStyle = { color: cyan, fontSize: "10px", letterSpacing: "1px", display: "block", marginBottom: "4px" };
const btn = {
  minHeight: "44px",
  padding: "0 14px",
  borderRadius: "6px",
  fontSize: "11px",
  fontWeight: "700",
  letterSpacing: "1px",
  cursor: "pointer",
};
const primaryBtn = { ...btn, background: "linear-gradient(135deg, #00f5ff, #0080ff)", color: "#020817", border: "none" };
const ghostBtn = { ...btn, background: "none", color: "rgba(0,245,255,0.7)", border: `1px solid ${border}` };

export function useMoney() {
  const { i18n } = useTranslation();
  return useCallback(
    (n) =>
      n === null || n === undefined
        ? "—"
        : Number(n).toLocaleString(i18n.language === "es" ? "es-US" : "en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }),
    [i18n.language],
  );
}

function ProgressBar({ pct, status }) {
  const width = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ height: "10px", borderRadius: "5px", backgroundColor: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
      <div style={{ width: `${width}%`, height: "100%", backgroundColor: afeStatusColor(status) }} />
    </div>
  );
}

function AfeForm({ initial, wellId, token, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({
    afeNumber: initial?.afeNumber || "",
    description: initial?.description || "",
    type: initial?.type || "workover",
    approvedAmount: initial?.approvedAmount ?? "",
    approvalDate: initial?.approvalDate ? String(initial.approvalDate).slice(0, 10) : "",
    status: initial?.status || "open",
    categoryBudgets: Object.fromEntries(
      AFE_CATEGORIES.map((c) => [c, initial?.categoryBudgets?.[c] ?? ""]),
    ),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!form.afeNumber.trim()) return setError(t("afe.form.numberRequired"));
    if (form.approvedAmount === "" || Number(form.approvedAmount) < 0 || Number.isNaN(Number(form.approvedAmount))) {
      return setError(t("afe.form.amountRequired"));
    }
    setSaving(true);
    setError("");
    const body = {
      ...form,
      approvedAmount: Number(form.approvedAmount),
      approvalDate: form.approvalDate || null,
      categoryBudgets: Object.fromEntries(
        AFE_CATEGORIES.map((c) => [c, form.categoryBudgets[c] === "" ? null : Number(form.categoryBudgets[c])]),
      ),
    };
    try {
      const headers = { Authorization: "Bearer " + token };
      if (initial?._id) await axios.put(`${API}/api/afes/${initial._id}`, body, { headers });
      else await axios.post(`${API}/api/afes`, { ...body, wellId }, { headers });
      onSaved();
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 403
          ? t("afe.form.forbidden")
          : err.response?.data?.details?.[0]?.message || err.response?.data?.error || t("afe.form.saveFailed"),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: `1px solid ${cyan}55`, borderRadius: "10px", padding: "16px", marginBottom: "14px", backgroundColor: "rgba(0,245,255,0.05)" }}>
      <div style={{ color: cyan, fontSize: "12px", fontWeight: "700", letterSpacing: "2px", marginBottom: "12px" }}>
        {initial?._id ? t("afe.form.editTitle", { number: initial.afeNumber }) : t("afe.form.newTitle")}
      </div>
      <ErrorBanner message={error} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
        <label>
          <span style={labelStyle}>{t("afe.form.number")}</span>
          <input style={inputStyle} value={form.afeNumber} onChange={(e) => set("afeNumber", e.target.value)} />
        </label>
        <label>
          <span style={labelStyle}>{t("afe.form.type")}</span>
          <select style={inputStyle} value={form.type} onChange={(e) => set("type", e.target.value)}>
            {AFE_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`afe.types.${ty}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span style={labelStyle}>{t("afe.form.approvedAmount")}</span>
          <input
            style={inputStyle}
            type="number"
            inputMode="decimal"
            min="0"
            value={form.approvedAmount}
            onChange={(e) => set("approvedAmount", e.target.value)}
          />
        </label>
        <label>
          <span style={labelStyle}>{t("afe.form.approvalDate")}</span>
          <input style={inputStyle} type="date" value={form.approvalDate} onChange={(e) => set("approvalDate", e.target.value)} />
        </label>
        <label>
          <span style={labelStyle}>{t("afe.form.status")}</span>
          <select style={inputStyle} value={form.status} onChange={(e) => set("status", e.target.value)}>
            <option value="open">{t("afe.status.open")}</option>
            <option value="closed">{t("afe.status.closed")}</option>
          </select>
        </label>
      </div>
      <label style={{ display: "block", marginTop: "12px" }}>
        <span style={labelStyle}>{t("afe.form.description")}</span>
        <input style={inputStyle} value={form.description} onChange={(e) => set("description", e.target.value)} />
      </label>
      <div style={{ ...labelStyle, marginTop: "14px" }}>{t("afe.form.categoryBudgets")}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
        {AFE_CATEGORIES.map((c) => (
          <label key={c}>
            <span style={{ ...labelStyle, color: "rgba(255,255,255,0.6)" }}>{t(`afe.categories.${c}`)}</span>
            <input
              style={inputStyle}
              type="number"
              inputMode="decimal"
              min="0"
              aria-label={t(`afe.categories.${c}`)}
              value={form.categoryBudgets[c]}
              onChange={(e) => set("categoryBudgets", { ...form.categoryBudgets, [c]: e.target.value })}
            />
          </label>
        ))}
      </div>
      <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
        <button type="button" style={primaryBtn} onClick={save} disabled={saving}>
          {saving ? t("afe.form.saving") : t("afe.form.save")}
        </button>
        <button type="button" style={ghostBtn} onClick={onCancel}>
          {t("afe.form.cancel")}
        </button>
      </div>
    </div>
  );
}

function AfeSummary({ afe, canManage, onEdit, onDelete }) {
  const { t } = useTranslation();
  const money = useMoney();
  const s = afe.summary;
  const color = afeStatusColor(s.status);
  const under = s.overUnder >= 0;
  const stat = (label, value, valueColor = "white") => (
    <div style={{ minWidth: 0 }}>
      <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "10px", letterSpacing: "1px", textTransform: "uppercase" }}>{label}</div>
      <div style={{ color: valueColor, fontSize: "16px", fontWeight: "800", overflowWrap: "anywhere" }}>{value}</div>
    </div>
  );

  return (
    <div
      data-testid="afe-summary"
      style={{ border: `1px solid ${color}66`, borderRadius: "10px", padding: "14px", marginBottom: "12px", backgroundColor: `${color}0d` }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "white", fontWeight: "800", fontSize: "15px" }}>
            {afe.afeNumber} · {t(`afe.types.${afe.type}`)}
          </div>
          {afe.description && <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", marginTop: "2px" }}>{afe.description}</div>}
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px", marginTop: "2px" }}>
            {t(`afe.status.${afe.status}`)}
            {afe.approvalDate
              ? ` · ${t("afe.approved", { date: formatDateUTC(afe.approvalDate) })}`
              : ""}
          </div>
        </div>
        <span
          style={{
            color,
            border: `1px solid ${color}`,
            borderRadius: "20px",
            padding: "4px 10px",
            fontSize: "10px",
            fontWeight: "800",
            letterSpacing: "1px",
            whiteSpace: "nowrap",
          }}
        >
          {t(`afe.statusFlag.${s.status}`)}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", margin: "12px 0" }}>
        {stat(t("afe.budget"), money(s.budget))}
        {stat(t("afe.actual"), money(s.actual))}
        {stat(
          t("afe.overUnder"),
          t(under ? "afe.under" : "afe.over", { amount: money(Math.abs(s.overUnder)), pct: Math.abs(s.overUnderPct ?? 0) }),
          under ? (s.status === "near" ? "#ffaa00" : "#00cc66") : "#ff4466",
        )}
      </div>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", margin: "-4px 0 10px" }}>{t("afe.actualDef")}</div>
      <ProgressBar pct={s.pctSpent} status={s.status} />
      <div style={{ color, fontSize: "12px", fontWeight: "700", marginTop: "6px" }}>{t("afe.pctSpent", { pct: s.pctSpent ?? 0 })}</div>
      {s.rigReportActual > 0 && (
        <div data-testid="afe-rig-reports" style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px", marginTop: "4px" }}>
          {t("afe.includesRigReports", { amount: money(s.rigReportActual) })}
        </div>
      )}

      {s.categories.length > 0 && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ color: cyan, fontSize: "10px", letterSpacing: "2px", marginBottom: "6px" }}>{t("afe.byCategory")}</div>
          {s.categories.map((c) => (
            <div key={c.category} style={{ padding: "6px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", fontSize: "12px" }}>
                <span style={{ color: "white" }}>{t(`afe.categories.${c.category}`)}</span>
                <span style={{ color: c.status ? afeStatusColor(c.status) : "rgba(255,255,255,0.7)", whiteSpace: "nowrap" }}>
                  {money(c.actual)} / {c.budget === null ? t("afe.noBudget") : money(c.budget)}
                </span>
              </div>
              {c.budget !== null && (
                <div style={{ marginTop: "4px" }}>
                  <ProgressBar pct={c.pctSpent} status={c.status} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div style={{ display: "flex", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
          <button type="button" style={ghostBtn} onClick={() => onEdit(afe)}>
            {t("afe.edit")}
          </button>
          <button type="button" style={{ ...ghostBtn, color: "#ff4466", borderColor: "#ff446655" }} onClick={() => onDelete(afe)}>
            {t("afe.delete")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AfeCard({ wellId, token, user }) {
  const { t } = useTranslation();
  const [afes, setAfes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (new) | afe
  const canManage = canManageAfes(user);
  const headers = { Authorization: "Bearer " + token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/afes`, { headers: { Authorization: "Bearer " + token }, params: { wellId } });
      setAfes(res.data);
      setError("");
    } catch {
      setError(t("afe.loadError"));
    } finally {
      setLoading(false);
    }
  }, [wellId, token, t]);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(afe) {
    if (!window.confirm(t("afe.confirmDelete", { number: afe.afeNumber }))) return;
    try {
      await axios.delete(`${API}/api/afes/${afe._id}`, { headers });
      load();
    } catch (err) {
      setError(err.response?.status === 403 ? t("afe.form.forbidden") : err.response?.data?.error || t("afe.form.saveFailed"));
    }
  }

  async function download(format) {
    try {
      const res = await fetch(`${API}/api/afes/export?wellId=${wellId}&format=${format}`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `AFE_vs_Actual.${format === "csv" ? "csv" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      setError(t("afe.exportFailed"));
    }
  }

  return (
    <section
      data-testid="afe-card"
      style={{ backgroundColor: card, border: `1px solid ${border}`, borderRadius: "12px", padding: "16px", marginBottom: "20px" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
        <h3 style={{ color: cyan, fontSize: "13px", letterSpacing: "2px", margin: 0 }}>{t("afe.title")}</h3>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {afes.length > 0 && (
            <>
              <button type="button" style={ghostBtn} onClick={() => download("csv")}>
                {t("afe.exportCsv")}
              </button>
              <button type="button" style={ghostBtn} onClick={() => download("xlsx")}>
                {t("afe.exportExcel")}
              </button>
            </>
          )}
          {canManage && !editing && (
            <button type="button" style={primaryBtn} onClick={() => setEditing({})}>
              {t("afe.newAfe")}
            </button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      {editing && (
        <AfeForm
          initial={editing._id ? editing : null}
          wellId={wellId}
          token={token}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {loading ? (
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>{t("afe.loading")}</div>
      ) : afes.length === 0 ? (
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "12px" }}>{canManage ? t("afe.none") : t("afe.noneReadOnly")}</div>
      ) : (
        afes.map((afe) => <AfeSummary key={afe._id} afe={afe} canManage={canManage} onEdit={setEditing} onDelete={remove} />)
      )}
    </section>
  );
}
